import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency } from "@/components/StatusBadge";
import { RotateCcw, Lock, Unlock, Pencil, Trash2, History, ExternalLink } from "lucide-react";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

// Standardized payment methods across the app (accounts requested a fixed list)
import { useTableSort } from "@/lib/useTableSort";
import { usePersistentSort } from "@/lib/usePersistentSort";
const PAYMENT_METHOD_OPTIONS = [
  ["wire_ach", "Wire / ACH"],
  ["cc_stripe", "CC (Stripe)"],
  ["paypal", "PayPal"],
  ["payoneer", "Payoneer"],
  ["wire_in", "Wire IN"],
  ["others", "Others"],
];
const PAYMENT_METHODS = PAYMENT_METHOD_OPTIONS.map(([v]) => v);

export default function PaymentsReceived() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);   // txn currently being edited
  const [editForm, setEditForm] = useState({});
  const [editLastFx, setEditLastFx] = useState(null); // "rate" | "inr" — bi-dir field priority
  const [editPriorTxns, setEditPriorTxns] = useState([]); // prior payments on this invoice
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);
  const canEdit = hasRole(user, "admin", "accounts");

  const refresh = useCallback(
    () => api.get("/api/payments").then((r) => setItems(r.data || [])),
    []
  );

  const openHistory = async (t) => {
    if (!t.invoice_id) return;
    setHistoryFor(t); setHistory(null);
    try {
      const { data } = await api.get(`/api/invoices/${t.invoice_id}/payment-history`);
      setHistory(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load history"); }
  };

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [bdFilter, setBdFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  // "Awaiting INR" — shows only non-INR payments whose amount_received_inr is
  // still blank/zero (accounts hasn't back-filled after the actual bank credit).
  const [pendingInrOnly, setPendingInrOnly] = useState(false);
  // Bulk INR back-fill selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRate, setBulkRate] = useState("");
  const [bulkOverrides, setBulkOverrides] = useState({}); // {txn_id: "1234.56"}
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    refresh();
    api.get("/api/clients").then((r) => setClients(r.data || []));
    api.get("/api/projects").then((r) => setProjects(r.data || []));
    api.get("/api/users").then((r) => setUsers(r.data || [])).catch((e) => {
      if (e.response?.status && e.response.status !== 403) logger.error("users fetch failed", e);
    });
  }, [refresh]);

  // Deep-link support: `?edit=<txn_id>` auto-opens the Edit dialog for that
  // payment once the transactions list finishes loading. Used by the Payment
  // History timeline on the Invoices page so users don't have to hunt for the
  // row manually.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // ?filter=pending-inr — turns on the "Awaiting INR" toggle. Handled before
    // items load so the filter is active as soon as data arrives.
    if (params.get("filter") === "pending-inr") setPendingInrOnly(true);
    // Strip only the filter param so ?edit=... below still runs on this render.
    if (params.get("filter")) {
      params.delete("filter");
      const q = params.toString();
      navigate(location.pathname + (q ? `?${q}` : ""), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!items.length) return;
    const params = new URLSearchParams(location.search);
    const target = params.get("edit");
    if (!target) return;
    const t = items.find((x) => x.id === target);
    if (t) {
      openEdit(t);
      // Strip the query param so a page refresh doesn't reopen the dialog.
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, location.search]);

  const toggleLock = async (t) => {
    const url = t.incentive_locked ? `/api/incentives/unlock/${t.id}` : `/api/incentives/lock/${t.id}`;
    try {
      const { data } = await api.post(url);
      toast.success(data.incentive_locked ? "Locked — incentive removed" : "Unlocked — incentive computed");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (t) => {
    setEditing(t);
    // Prefill fx_rate from stored INR if both present (allows editing either)
    let fx = "";
    if (t.amount_received_inr != null && t.amount_received && Number(t.amount_received) > 0 && t.currency !== "INR") {
      fx = String(Number((Number(t.amount_received_inr) / Number(t.amount_received)).toFixed(6)));
    }
    setEditLastFx(null);
    setEditForm({
      amount_received: t.amount_received,
      amount_received_inr: t.amount_received_inr ?? "",
      fx_rate: fx,
      payment_date: t.payment_date?.slice(0, 10) || "",
      payment_method: t.payment_method || "",
      reference: t.reference || "",
      notes: t.notes || "",
    });
    // Fetch prior payments on the same invoice so we can surface rate chips.
    // Empty on failure — chips just won't render, no error toast needed.
    setEditPriorTxns([]);
    if (t.invoice_id) {
      api.get(`/api/invoices/${t.invoice_id}`)
        .then((r) => setEditPriorTxns(r.data?.transactions || []))
        .catch(() => setEditPriorTxns([]));
    }
  };
  // Up to 2 unique rates from PRIOR payments (excluding the txn being edited).
  // Rates outside the sane 0.01–500 band are treated as data-entry outliers and
  // filtered out so we never surface a nonsense rate to the accountant.
  const editRateHistory = useMemo(() => {
    if (!editing || !editing.currency || editing.currency === "INR") return [];
    const seen = new Set();
    const list = [];
    for (const t of editPriorTxns) {
      if (t.id === editing.id) continue;
      if (!t.amount_received || !t.amount_received_inr) continue;
      if (t.currency && t.currency !== editing.currency) continue;
      const rate = Number(t.amount_received_inr) / Number(t.amount_received);
      if (!Number.isFinite(rate) || rate < 0.01 || rate > 500) continue;
      const key = rate.toFixed(4);
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({ rate: Number(rate.toFixed(6)), date: (t.payment_date || t.created_at || "").slice(0, 10) });
      if (list.length >= 2) break;
    }
    return list;
  }, [editPriorTxns, editing]);
  // Bi-directional link: amount × rate = inr (same rules as the Record dialog)
  const setEditAmount = (v) => {
    setEditForm((p) => {
      const next = { ...p, amount_received: v };
      const amt = Number(v);
      if (Number.isFinite(amt) && amt > 0) {
        if (editLastFx === "rate" && p.fx_rate !== "" && p.fx_rate !== null) {
          next.amount_received_inr = String(Number((amt * Number(p.fx_rate)).toFixed(2)));
        } else if (editLastFx === "inr" && p.amount_received_inr !== "" && p.amount_received_inr !== null) {
          next.fx_rate = String(Number((Number(p.amount_received_inr) / amt).toFixed(6)));
        }
      }
      return next;
    });
  };
  const setEditRate = (v) => {
    setEditLastFx("rate");
    setEditForm((p) => {
      const next = { ...p, fx_rate: v };
      const amt = Number(p.amount_received);
      const rate = Number(v);
      if (Number.isFinite(amt) && amt > 0 && Number.isFinite(rate) && v !== "") {
        next.amount_received_inr = String(Number((amt * rate).toFixed(2)));
      }
      return next;
    });
  };
  const setEditInr = (v) => {
    setEditLastFx("inr");
    setEditForm((p) => {
      const next = { ...p, amount_received_inr: v };
      const amt = Number(p.amount_received);
      const inr = Number(v);
      if (Number.isFinite(amt) && amt > 0 && Number.isFinite(inr) && v !== "") {
        next.fx_rate = String(Number((inr / amt).toFixed(6)));
      }
      return next;
    });
  };
  const saveEdit = async () => {
    if (Number(editForm.amount_received) < 0) return toast.error("Amount cannot be negative");
    try {
      const body = {
        amount_received: Number(editForm.amount_received),
        payment_date: editForm.payment_date || null,
        payment_method: editForm.payment_method || null,
        reference: editForm.reference || null,
        notes: editForm.notes || null,
      };
      // Only send INR when user typed something (blank = leave unchanged / clear).
      if (editForm.amount_received_inr !== "" && editForm.amount_received_inr !== null) {
        body.amount_received_inr = Number(editForm.amount_received_inr);
      }
      await api.patch(`/api/payments/${editing.id}`, body);
      toast.success("Payment updated — invoice, project totals & BD incentive re-synced");
      setEditing(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeTxn = async (t) => {
    if (!window.confirm(`Delete this payment of ${formatCurrency(t.amount_received, t.currency)}? Invoice outstanding will be restored.`)) return;
    try {
      await api.delete(`/api/payments/${t.id}`);
      toast.success("Payment deleted");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const projectMap = useMemo(() => {
    const m = new Map(); projects.forEach((p) => m.set(p.id, p)); return m;
  }, [projects]);
  const clientMap = useMemo(() => {
    const m = new Map(); clients.forEach((c) => m.set(c.id, c)); return m;
  }, [clients]);
  const userMap = useMemo(() => {
    const m = new Map(); users.forEach((u) => m.set(u.id, u)); return m;
  }, [users]);
  const bdOptions = useMemo(() => users.filter((u) => u.role === "business_development"), [users]);
  const projectOptions = useMemo(
    () => projects.filter((p) => clientFilter === "all" || p.client_id === clientFilter).slice(0, 200),
    [projects, clientFilter]
  );

  // Split the previously 8-dep memo into two smaller pieces so each memo's
  // dep list stays tiny and predictable. The filter object bundles the seven
  // filter primitives; `filtered` only depends on items + the filter bundle.
  const filterState = useMemo(
    () => ({ fromDate, toDate, clientFilter, projectFilter, bdFilter, entityFilter, pendingInrOnly }),
    [fromDate, toDate, clientFilter, projectFilter, bdFilter, entityFilter, pendingInrOnly]
  );

  const matchesFilter = useCallback((t) => {
    const { fromDate: f, toDate: to, clientFilter: cf, projectFilter: pf, bdFilter: bf, entityFilter: ef, pendingInrOnly: pi } = filterState;
    const date = (t.payment_date || "").slice(0, 10);
    if (f && date < f) return false;
    if (to && date > to) return false;
    const p = projectMap.get(t.project_id);
    if (cf !== "all" && p?.client_id !== cf) return false;
    if (pf !== "all" && t.project_id !== pf) return false;
    if (bf !== "all" && p?.business_development_owner_id !== bf) return false;
    if (ef !== "all" && (t.entity_type || "").toLowerCase() !== ef) return false;
    if (pi) {
      const cur = (t.currency || "").toUpperCase();
      const inr = Number(t.amount_received_inr || 0);
      if (cur === "INR" || inr > 0) return false;
    }
    return true;
  }, [filterState, projectMap]);

  // Row is bulk-back-fillable iff it's a non-INR payment with no INR total yet.
  const isPending = useCallback((t) => {
    const cur = (t.currency || "").toUpperCase();
    return cur && cur !== "INR" && !(Number(t.amount_received_inr || 0) > 0);
  }, []);

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const openBulk = () => {
    setBulkRate("");
    setBulkOverrides({});
    setBulkOpen(true);
  };
  const applyBulk = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    // Normalize overrides to numbers > 0
    const overrides = {};
    for (const [id, v] of Object.entries(bulkOverrides)) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) overrides[id] = n;
    }
    const rate = bulkRate === "" ? null : Number(bulkRate);
    if (rate == null && Object.keys(overrides).length === 0) {
      return toast.error("Enter a rate or at least one manual INR amount");
    }
    if (rate != null && (!Number.isFinite(rate) || rate <= 0)) {
      return toast.error("Rate must be > 0");
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/api/payments/bulk-inr-backfill", {
        transaction_ids: ids,
        rate,
        overrides: Object.keys(overrides).length ? overrides : null,
      });
      toast.success(`${data.updated} payment(s) back-filled${data.skipped_already_set ? ` · ${data.skipped_already_set} skipped (already set)` : ""}`);
      setBulkOpen(false);
      setSelectedIds(new Set());
      setBulkRate("");
      setBulkOverrides({});
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk back-fill failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = useMemo(() => items.filter(matchesFilter), [items, matchesFilter]);
  // Client-side sort. Defaults to payment_date desc (newest receipts on top).
  // Persisted per-user in localStorage so their choice survives reloads.
  const [sortBy, setSortBy] = usePersistentSort("payments", { key: "payment_date", dir: "desc" });
  const sortedRows = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const k = sortBy.key;
      let va, vb;
      if (k === "client") {
        va = clientMap.get(projectMap.get(a.project_id)?.client_id)?.firm_name;
        vb = clientMap.get(projectMap.get(b.project_id)?.client_id)?.firm_name;
      } else if (k === "project") {
        va = projectMap.get(a.project_id)?.project_name;
        vb = projectMap.get(b.project_id)?.project_name;
      } else {
        va = a?.[k]; vb = b?.[k];
      }
      const numeric = ["amount_received", "amount_received_inr"].includes(k);
      if (numeric) { va = Number(va || 0); vb = Number(vb || 0); }
      else { va = (va ?? "").toString().toLowerCase(); vb = (vb ?? "").toString().toLowerCase(); }
      if (va < vb) return sortBy.dir === "asc" ? -1 : 1;
      if (va > vb) return sortBy.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortBy, clientMap, projectMap]);
  const sortH = (key, label) => (
    <button
      type="button"
      onClick={() => setSortBy((p) => p.key === key
        ? { key, dir: p.dir === "asc" ? "desc" : "asc" }
        : { key, dir: ["amount_received", "amount_received_inr", "payment_date"].includes(key) ? "desc" : "asc" })}
      className="inline-flex items-center gap-1 hover:text-slate-900"
      data-testid={`sort-${key}`}
    >
      {label}
      <span className="text-[10px] text-slate-400">{sortBy.key === key ? (sortBy.dir === "asc" ? "▲" : "▼") : "↕"}</span>
    </button>
  );

  const currencyTotals = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const cur = t.currency || "USD";
      map[cur] = (map[cur] || 0) + Number(t.amount_received || 0);
    });
    return Object.entries(map).map(([currency, total]) => ({ currency, total })).sort((a, b) => a.currency.localeCompare(b.currency));
  }, [filtered]);

  const resetFilters = () => {
    setFromDate(""); setToDate("");
    setClientFilter("all"); setProjectFilter("all"); setBdFilter("all"); setEntityFilter("all");
    setPendingInrOnly(false);
  };

  return (
    <div className="space-y-6" data-testid="payments-received-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight">Payments Received</h1>
        <p className="text-sm text-slate-500 mt-1">All recorded payment transactions with per-currency totals. Currencies are shown separately — never combined unless a verified conversion feature is enabled.</p>
      </div>

      {/* Filters — scroll away naturally, not sticky (§2) */}
      <Card className="border-slate-200 shadow-none p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">From</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="pay-from-date" /></div>
          <div className="space-y-1"><Label className="text-[10px] uppercase text-slate-500">To</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="pay-to-date" /></div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Client</Label>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger data-testid="pay-client-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.firm_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Project</Label>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger data-testid="pay-project-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projectOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">BD</Label>
            <Select value={bdFilter} onValueChange={setBdFilter}>
              <SelectTrigger data-testid="pay-bd-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All BDs</SelectItem>
                {bdOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Entity</Label>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger data-testid="pay-entity-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entities</SelectItem>
                <SelectItem value="llc">LLC</SelectItem>
                <SelectItem value="india">India</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-3 lg:col-span-5 flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={resetFilters} data-testid="pay-reset-filters">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset filters
              </Button>
              <Select
                value={sortBy.key === "payment_date" ? (sortBy.dir === "desc" ? "newest" : "oldest") : "custom"}
                onValueChange={(v) => {
                  if (v === "newest") setSortBy({ key: "payment_date", dir: "desc" });
                  else if (v === "oldest") setSortBy({ key: "payment_date", dir: "asc" });
                }}
              >
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="pay-sort-order"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  {sortBy.key !== "payment_date" && <SelectItem value="custom" disabled>Custom · {sortBy.key}</SelectItem>}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => setPendingInrOnly((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition ${
                  pendingInrOnly
                    ? "bg-brand text-white border border-brand"
                    : "bg-white text-slate-600 border border-slate-200 hover:border-brand/40"
                }`}
                data-testid="pay-pending-inr-toggle"
                title="Show only non-INR payments where the actual INR amount is still blank"
              >
                {pendingInrOnly && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                Awaiting INR only
              </button>
              {canEdit && selectedIds.size > 0 && (
                <Button
                  size="sm"
                  onClick={openBulk}
                  className="bg-brand hover:bg-brand-hover"
                  data-testid="bulk-backfill-btn"
                >
                  Back-fill INR ({selectedIds.size})
                </Button>
              )}
            </div>
            <div className="text-xs text-slate-500">
              {filtered.length.toLocaleString()} of {items.length.toLocaleString()} transaction{filtered.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </Card>

      {/* Per-currency totals */}
      <Card className="border-slate-200 shadow-none p-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Total received (filtered) — currencies shown separately</div>
        {currencyTotals.length === 0 ? (
          <div className="text-slate-400 text-sm">No transactions match the current filters.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="pay-totals-by-currency">
            {currencyTotals.map(({ currency, total }) => (
              <div key={currency} className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid={`pay-total-${currency}`}>
                <div className="text-[10px] uppercase text-emerald-700">Total {currency}</div>
                <div className="text-xl font-semibold text-emerald-900 tabular-nums mt-0.5">{formatCurrency(total, currency)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Table — fixed-height viewport keeps horizontal scrollbar visible without
          scrolling past 100+ rows; <thead> sticks so column labels stay in view. */}
      <Card className="border-slate-200 shadow-none p-0 overflow-hidden">
        <div
          className="overflow-auto max-h-[calc(100vh-320px)] min-h-[320px]"
          data-testid="payments-scroll"
        >
          <table className="data-table min-w-[1500px] w-full">
            <colgroup>
              <col style={{ width: 96 }} />{/* Date */}
              <col style={{ width: 170 }} />{/* Client */}
              <col style={{ width: 220 }} />{/* Project */}
              <col style={{ width: 130 }} />{/* Invoice # */}
              <col style={{ width: 70 }} />{/* Entity */}
              <col style={{ width: 120 }} />{/* Amount */}
              <col style={{ width: 60 }} />{/* Currency */}
              <col style={{ width: 130 }} />{/* INR Received */}
              <col style={{ width: 90 }} />{/* Method */}
              <col style={{ width: 130 }} />{/* Reference */}
              <col style={{ width: 170 }} />{/* BD Incentive */}
              {canEdit && <col style={{ width: 96 }} />}{/* Actions (last) */}
            </colgroup>
            <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_rgba(15,23,42,0.08)]"><tr>
              {canEdit && (pendingInrOnly || selectedIds.size > 0) && (
                <th className="bg-slate-50 w-8 text-center">
                  <input
                    type="checkbox"
                    aria-label="Select all pending"
                    checked={filtered.filter(isPending).length > 0 && filtered.filter(isPending).every((t) => selectedIds.has(t.id))}
                    onChange={(e) => {
                      const pend = filtered.filter(isPending).map((t) => t.id);
                      setSelectedIds(e.target.checked ? new Set(pend) : new Set());
                    }}
                    data-testid="bulk-select-all"
                  />
                </th>
              )}
              <th className="bg-slate-50">{sortH("payment_date", "Date")}</th><th className="bg-slate-50">{sortH("client", "Client")}</th><th className="bg-slate-50">{sortH("project", "Project")}</th><th className="bg-slate-50">{sortH("invoice_number", "Invoice #")}</th><th className="bg-slate-50">{sortH("entity_type", "Entity")}</th><th className="text-right bg-slate-50">{sortH("amount_received", "Amount")}</th><th className="bg-slate-50">{sortH("currency", "Currency")}</th><th className="bg-slate-50">{sortH("amount_received_inr", "INR Received")}</th><th className="bg-slate-50">{sortH("payment_method", "Method")}</th><th className="bg-slate-50">{sortH("reference", "Reference")}</th><th className="bg-slate-50">BD Incentive</th>
              {canEdit && <th className="w-24 bg-slate-50 text-right">Actions</th>}
            </tr></thead>
            <tbody>
              {sortedRows.map((t) => {
                const p = projectMap.get(t.project_id);
                const c = p ? clientMap.get(p.client_id) : null;
                const inrDisplay = t.currency === "INR"
                  ? formatCurrency(t.amount_received, "INR")
                  : (t.amount_received_inr != null ? formatCurrency(t.amount_received_inr, "INR") : null);
                return (
                  <tr key={t.id} data-testid={`payment-row-${t.id}`}>
                    {canEdit && (pendingInrOnly || selectedIds.size > 0) && (
                      <td className="text-center">
                        {isPending(t) ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(t.id)}
                            onChange={() => toggleSelect(t.id)}
                            aria-label="Select for INR back-fill"
                            data-testid={`bulk-select-${t.id}`}
                          />
                        ) : (
                          <span className="text-slate-300" title="Already has INR / is INR currency">·</span>
                        )}
                      </td>
                    )}
                    <td>{t.payment_date?.slice(0, 10)}</td>
                    <td className="truncate max-w-[180px]">
                      {c ? (
                        <button className="text-left hover:underline text-slate-800"
                                onClick={() => navigate(`/clients/${c.id}`)}
                                data-testid={`open-client-${c.id}`}
                                title={`Go to ${c.firm_name}`}>{c.firm_name}</button>
                      ) : "—"}
                    </td>
                    <td className="truncate max-w-[220px]">
                      {p ? (
                        <button className="text-left hover:underline text-slate-800 inline-flex items-center gap-1"
                                onClick={() => navigate(`/projects/${p.id}`)}
                                data-testid={`open-project-${p.id}`}
                                title={`Go to project ${p.project_name}`}>
                          {p.project_name}
                          <ExternalLink className="w-3 h-3 opacity-40" />
                        </button>
                      ) : "—"}
                    </td>
                    <td>
                      {t.invoice_number ? (
                        <button className="font-mono text-xs hover:underline text-slate-800 inline-flex items-center gap-1"
                                onClick={() => openHistory(t)}
                                data-testid={`open-history-${t.id}`}
                                title="View this invoice's full payment history">
                          {t.invoice_number}
                          <History className="w-3 h-3 opacity-40" />
                        </button>
                      ) : "—"}
                    </td>
                    <td className="text-xs uppercase">{t.entity_type === "india" ? "India" : "LLC"}</td>
                    <td className="text-right tabular-nums text-emerald-700 font-medium">{formatCurrency(t.amount_received, t.currency)}</td>
                    <td className="text-slate-500 text-xs">{t.currency}</td>
                    <td className="text-xs">
                      {inrDisplay ? (
                        <span className="text-emerald-700 tabular-nums">{inrDisplay}</span>
                      ) : (
                        canEdit ? (
                          <button className="italic text-slate-400 hover:text-slate-800 hover:underline"
                                  onClick={() => openEdit(t)}
                                  data-testid={`add-inr-${t.id}`}
                                  title="Not converted yet — click to add the actual INR credited to bank">
                            not converted · add
                          </button>
                        ) : (
                          <span className="italic text-slate-400">not converted</span>
                        )
                      )}
                    </td>
                    <td>{t.payment_method?.replace(/_/g, " ")}</td>
                    <td className="text-slate-500">{t.reference || "—"}</td>
                    <td className="text-xs">
                      {t.incentive_locked ? (
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 text-[10px]" title={t.incentive_lock_reason || "Locked — does not count for BD incentive"}>
                            <Lock className="w-3 h-3" /> locked
                          </span>
                          {canEdit && (
                            <Button size="sm" variant="ghost" onClick={() => toggleLock(t)} className="h-6 px-2 text-[10px]" data-testid={`unlock-txn-${t.id}`}>
                              <Unlock className="w-3 h-3 mr-1" />Unlock
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          {t.incentive ? (
                            <span className="text-emerald-700 tabular-nums" title={`${t.incentive.applied_percentage}% of ₹${t.incentive.amount_received_inr}`}>
                              ₹{t.incentive.incentive_inr?.toLocaleString?.() || 0}
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                          {canEdit && (
                            <Button size="sm" variant="ghost" onClick={() => toggleLock(t)} className="h-6 px-2 text-[10px]" data-testid={`lock-txn-${t.id}`}>
                              <Lock className="w-3 h-3 mr-1" />Lock
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                    {canEdit && (
                      <td className="whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-0.5 rounded-md border border-slate-200 bg-white">
                          <button title="Edit — amount, INR conversion, date, reference, notes" onClick={() => openEdit(t)}
                                  className="px-2 py-1 text-slate-700 hover:bg-slate-50 border-r border-slate-200"
                                  data-testid={`edit-payment-${t.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button title="Delete payment (soft-delete — invoice totals restore)" onClick={() => removeTxn(t)}
                                  className="px-2 py-1 text-rose-500 hover:bg-rose-50"
                                  data-testid={`delete-payment-${t.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={canEdit ? 12 : 11} className="text-center text-slate-500 py-8">No payments match the current filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Payment history dialog — reuses the shared timeline UX from Invoices */}
      <Dialog open={!!historyFor} onOpenChange={(v) => { if (!v) { setHistoryFor(null); setHistory(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Payment History · {historyFor?.invoice_number}</DialogTitle></DialogHeader>
          {!history ? (
            <div className="text-slate-500 py-6 text-center">Loading…</div>
          ) : history.timeline.length === 0 ? (
            <div className="text-slate-500 py-6 text-center">No payments recorded yet.</div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 flex gap-4 border-b pb-2">
                <span>Received: <b className="text-emerald-700">{formatCurrency(history.amount_received || 0, historyFor?.currency)}</b></span>
                <span>Outstanding: <b className="text-amber-700">{formatCurrency(history.outstanding || 0, historyFor?.currency)}</b></span>
                <span className="ml-auto text-slate-400">{history.timeline.length} event{history.timeline.length === 1 ? "" : "s"}</span>
              </div>
              <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4 pl-4 pt-2">
                {history.timeline.map((ev, idx) => {
                  const iconTone =
                    ev.type === "received" && !ev.is_deleted ? "bg-emerald-500" :
                    ev.type === "edited" ? "bg-amber-500" :
                    ev.type === "deleted" ? "bg-rose-500" : "bg-slate-300";
                  return (
                    <li key={ev.at || ev.txn_id || `event-${idx}`} className="relative">
                      <span className={`absolute -left-[22px] top-1.5 w-3 h-3 rounded-full ${iconTone} border-2 border-white`}></span>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="text-sm font-medium capitalize">
                            {ev.type === "received" && (ev.is_deleted ? "Payment received (later deleted)" : "Payment received")}
                            {ev.type === "edited" && "Payment edited"}
                            {ev.type === "deleted" && "Payment deleted"}
                          </div>
                          {ev.type === "received" && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              <span className="text-emerald-700 font-medium">{formatCurrency(ev.amount, ev.currency)}</span>
                              {ev.payment_method && <> · {ev.payment_method.replace(/_/g, " ")}</>}
                              {ev.reference && <> · ref {ev.reference}</>}
                              {ev.payment_date && <> · dated {ev.payment_date.slice(0, 10)}</>}
                            </div>
                          )}
                          {ev.type === "edited" && (
                            <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                              {(() => {
                                const before = ev.changes?.before || {};
                                const after = ev.changes?.after || {};
                                const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
                                  .filter((k) => !["updated_at", "last_edited_by", "last_edited_by_snapshot"].includes(k));
                                return keys.map((k) => (
                                  <div key={k}>
                                    <b className="text-slate-700">{k.replace(/_/g, " ")}</b>:{" "}
                                    <span className="line-through text-slate-400">{String(before[k] ?? "—")}</span>
                                    <span className="mx-1">→</span>
                                    <span className="text-slate-800 font-medium">{String(after[k] ?? "—")}</span>
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                          {ev.type === "deleted" && (
                            <div className="text-xs text-slate-500 mt-0.5">
                              Removed <span className="text-rose-700 font-medium">{formatCurrency(ev.amount, ev.currency)}</span>.
                            </div>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400 whitespace-nowrap text-right">
                          {ev.at && new Date(ev.at).toLocaleString()}
                          {ev.actor && <div>by {ev.actor}</div>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit payment dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Payment · {editing?.invoice_number || editing?.id?.slice(0, 8)}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 text-xs text-slate-500 bg-slate-50 rounded px-3 py-2">
                Correcting this entry re-syncs the invoice's <b>received</b> and <b>outstanding</b> plus the project's totals. If the row had a BD incentive, it is re-computed on the new amount (unless locked).
              </div>
              <div className="space-y-1.5">
                <Label>Amount received <span className="text-slate-400 text-xs">({editing?.currency || ""})</span></Label>
                <Input type="number" step="any" value={editForm.amount_received}
                       onChange={(e) => setEditAmount(e.target.value)}
                       data-testid="edit-payment-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Payment date</Label>
                <Input type="date" value={editForm.payment_date}
                       onChange={(e) => setEditForm({ ...editForm, payment_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select value={editForm.payment_method || ""} onValueChange={(v) => setEditForm({ ...editForm, payment_method: v })}>
                  <SelectTrigger data-testid="edit-payment-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHOD_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Reference</Label>
                <Input value={editForm.reference}
                       onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })}
                       placeholder="txn id / cheque no" />
              </div>
              {/* Bi-directional FX conversion — either the rate or the INR amount
                  can be edited; the other auto-fills. Non-INR payments rely on
                  this to compute the BD incentive on the true INR amount. */}
              {editing?.currency && editing.currency !== "INR" ? (
                <div className="col-span-2 space-y-2 rounded-md border border-slate-200 bg-slate-50/50 p-3">
                  <div className="text-xs font-medium text-slate-600 flex items-center gap-2">
                    <span>{editing.currency} → INR conversion</span>
                    <span className="text-[10px] text-slate-400 font-normal">enter either — the other auto-fills</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Conversion rate</Label>
                      {editRateHistory.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 pb-1" data-testid="edit-rate-history-chips">
                          <span className="text-[10px] uppercase tracking-wide text-slate-400">Recent:</span>
                          {editRateHistory.map((h, i) => (
                            <button
                              type="button"
                              key={`edit-rate-chip-${i}`}
                              onClick={() => setEditRate(String(h.rate))}
                              className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-brand/30 bg-brand-tint text-brand-dark hover:bg-brand hover:text-white text-[11px] font-medium transition"
                              title={`Reuse rate ${h.rate} from ${h.date || "prior payment"}`}
                              data-testid={`edit-rate-chip-${i}`}
                            >
                              {h.rate}
                              {h.date && <span className="text-[9px] font-normal opacity-70 group-hover:opacity-100">· {h.date}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">1 {editing.currency} =</span>
                        <Input
                          data-testid="edit-payment-fx-rate"
                          type="number"
                          step="0.000001"
                          value={editForm.fx_rate ?? ""}
                          onChange={(e) => setEditRate(e.target.value)}
                          placeholder="e.g. 83.50"
                          className="flex-1"
                        />
                        <span className="text-xs text-slate-500 whitespace-nowrap">INR</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Actual INR received</Label>
                      <Input
                        data-testid="edit-payment-inr"
                        type="number"
                        step="0.01"
                        value={editForm.amount_received_inr}
                        onChange={(e) => setEditInr(e.target.value)}
                        placeholder="exact ₹ credited to bank"
                      />
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Used for BD incentive calculation. Adding or changing either value re-computes the incentive automatically (unless the row is locked).
                  </div>
                </div>
              ) : (
                <div className="col-span-2 space-y-1.5">
                  <Label>Actual INR Received</Label>
                  <Input
                    data-testid="edit-payment-inr"
                    type="number"
                    step="0.01"
                    value={editForm.amount_received_inr}
                    onChange={(e) => setEditForm({ ...editForm, amount_received_inr: e.target.value })}
                    placeholder="Same as amount received"
                  />
                </div>
              )}
              <div className="col-span-2 space-y-1.5">
                <Label>Notes</Label>
                <Textarea value={editForm.notes} rows={2}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={saveEdit} data-testid="save-edit-payment">
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Bulk INR back-fill dialog ─────────────────────────────────────
          Either enter one FX rate (applied to every selected payment) OR
          fill an explicit INR for one or more rows — both can coexist. */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { if (!v && !bulkBusy) setBulkOpen(false); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 shrink-0">
            <DialogTitle>Back-fill INR on {selectedIds.size} payment{selectedIds.size === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              Enter one FX rate to auto-fill INR for every selected payment, or type an exact INR amount on the row(s) where you already know the bank credit. The two options can be mixed — manual override wins for those rows and the rate covers the rest.
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs text-slate-500">Bulk FX rate (INR per unit foreign)</Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={bulkRate}
                  onChange={(e) => setBulkRate(e.target.value)}
                  placeholder="e.g. 83.5"
                  data-testid="bulk-rate-input"
                />
              </div>
              <div className="text-xs text-slate-500 pb-2">
                Applies to selected non-INR payments <b>without</b> a manual override below.
              </div>
            </div>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Invoice</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-right px-3 py-2">Preview INR</th>
                    <th className="text-right px-3 py-2 w-40">Manual override</th>
                  </tr>
                </thead>
                <tbody>
                  {items.filter((t) => selectedIds.has(t.id)).map((t) => {
                    const override = bulkOverrides[t.id];
                    const rateN = bulkRate === "" ? null : Number(bulkRate);
                    const overrideN = override === undefined || override === "" ? null : Number(override);
                    const previewInr = overrideN != null && overrideN > 0
                      ? overrideN
                      : (rateN != null && Number.isFinite(rateN) && rateN > 0 ? Number(t.amount_received || 0) * rateN : 0);
                    const source = overrideN != null && overrideN > 0 ? "override" : (previewInr > 0 ? "rate" : "");
                    return (
                      <tr key={t.id} className="border-t border-slate-100" data-testid={`bulk-row-${t.id}`}>
                        <td className="px-3 py-2 text-slate-700">{t.payment_date?.slice(0, 10)}</td>
                        <td className="px-3 py-2 font-mono">{t.invoice_number || t.reference || "—"}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(t.amount_received, t.currency)}</td>
                        <td className="px-3 py-2 text-right">
                          {previewInr > 0 ? (
                            <span title={`source: ${source}`}>{formatCurrency(previewInr, "INR")}</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Input
                            type="number"
                            step="0.01"
                            className="h-7 text-right"
                            placeholder="use rate"
                            value={override ?? ""}
                            onChange={(e) => setBulkOverrides((prev) => ({ ...prev, [t.id]: e.target.value }))}
                            data-testid={`bulk-override-${t.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={applyBulk} disabled={bulkBusy} data-testid="bulk-apply-btn">
              {bulkBusy ? "Applying…" : "Apply back-fill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
